/**
 * PremiumConfirmDialog.jsx
 * Premium Confirmation Dialog
 *
 * Design System Premium Fleet v2.0
 * Versatile confirmation dialog that can be used for:
 * - Simple confirmations (just onConfirm callback)
 * - Delete operations (with endpoint/itemId)
 * - Custom async operations
 */

import React, { useState, memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

/**
 * Variants configuration
 */
const VARIANTS = {
  danger: {
    icon: DeleteIcon,
    iconBg: (tokens) => tokens?.semantic?.error || '#f85149',
    confirmBg: (tokens) => tokens?.semantic?.error || '#f85149',
    confirmHover: '#da3633',
  },
  warning: {
    icon: WarningAmberIcon,
    iconBg: (tokens) => tokens?.semantic?.warning || '#f59e0b',
    confirmBg: (tokens) => tokens?.brand?.primary || '#00d4aa',
    confirmHover: '#00b894',
  },
  info: {
    icon: InfoIcon,
    iconBg: (tokens) => tokens?.semantic?.info || '#3b82f6',
    confirmBg: (tokens) => tokens?.brand?.primary || '#00d4aa',
    confirmHover: '#00b894',
  },
  success: {
    icon: CheckCircleIcon,
    iconBg: (tokens) => tokens?.semantic?.success || '#22c55e',
    confirmBg: (tokens) => tokens?.semantic?.success || '#22c55e',
    confirmHover: '#16a34a',
  },
};

const PremiumConfirmDialog = memo(({
  // Core props
  open,
  onClose,
  onResult,
  onConfirm,

  // Content
  title,
  message,
  warningText,

  // Delete mode props
  endpoint,
  itemId,

  // Customization
  variant = 'danger', // danger, warning, info, success
  confirmLabel,
  cancelLabel,
  confirmIcon,
  loadingLabel,

  // Behavior
  closeOnConfirm = true,
  showWarning = true,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Get variant config
  const variantConfig = VARIANTS[variant] || VARIANTS.danger;
  const IconComponent = variantConfig.icon;

  // Colors with fallback
  const colors = {
    surface: tokens?.surface?.elevated || '#161b22',
    border: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    textPrimary: tokens?.text?.primary || '#f0f6fc',
    textSecondary: tokens?.text?.secondary || '#8b949e',
    iconBg: variantConfig.iconBg(tokens),
    confirmBg: variantConfig.confirmBg(tokens),
    confirmHover: variantConfig.confirmHover,
    hover: tokens?.surface?.hover || 'rgba(255,255,255,0.05)',
  };

  // Default labels
  const defaultLabels = {
    title: variant === 'danger'
      ? (t('sharedConfirmDelete') || 'Confirmar Exclusão')
      : (t('sharedConfirm') || 'Confirmar'),
    message: variant === 'danger'
      ? (t('sharedConfirmDeleteMessage') || 'Tem certeza que deseja excluir este item?')
      : (t('sharedConfirmMessage') || 'Deseja continuar com esta ação?'),
    warning: t('sharedDeleteWarning') || 'Esta ação não pode ser desfeita.',
    confirm: variant === 'danger'
      ? (t('sharedRemove') || 'Excluir')
      : (t('sharedConfirm') || 'Confirmar'),
    cancel: t('sharedCancel') || 'Cancelar',
    loading: variant === 'danger'
      ? (t('sharedDeleting') || 'Excluindo...')
      : (t('sharedProcessing') || 'Processando...'),
  };

  const handleClose = useCallback(() => {
    if (loading) return;
    onClose?.();
    onResult?.(false);
  }, [loading, onClose, onResult]);

  const handleConfirm = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      // If endpoint and itemId are provided, do DELETE request
      if (endpoint && itemId) {
        const response = await fetch(`/api/${endpoint}/${itemId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      // Call custom onConfirm if provided
      if (onConfirm) {
        await onConfirm();
      }

      // Success
      onResult?.(true);

      if (closeOnConfirm) {
        onClose?.();
      }
    } catch (error) {
      console.error('PremiumConfirmDialog error:', error);
      setErrorMessage(error.message || 'Operação falhou');
    } finally {
      setLoading(false);
    }
  }, [loading, endpoint, itemId, onConfirm, onResult, onClose, closeOnConfirm]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={loading}
        disableEnforceFocus
        disableAutoFocus
        keepMounted={false}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            backgroundImage: 'none',
            m: 2,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(colors.iconBg, 0.2),
            }}
          >
            <IconComponent sx={{ color: colors.iconBg }} />
          </Box>
          {title || defaultLabels.title}
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: colors.textSecondary }}>
            {message || defaultLabels.message}
            {showWarning && variant === 'danger' && (
              <>
                <br />
                <strong style={{ color: colors.iconBg }}>
                  {warningText || defaultLabels.warning}
                </strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: colors.textSecondary,
              '&:hover': {
                backgroundColor: colors.hover,
              },
            }}
          >
            {cancelLabel || defaultLabels.cancel}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                confirmIcon || (variant === 'danger' ? <DeleteIcon /> : null)
              )
            }
            sx={{
              backgroundColor: colors.confirmBg,
              color: '#ffffff',
              minWidth: 120,
              '&:hover': {
                backgroundColor: colors.confirmHover,
              },
              '&:disabled': {
                backgroundColor: colors.confirmBg,
                opacity: 0.7,
              },
            }}
          >
            {loading
              ? (loadingLabel || defaultLabels.loading)
              : (confirmLabel || defaultLabels.confirm)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{
            backgroundColor: alpha(colors.iconBg, 0.15),
            borderRadius: '12px',
            border: `1px solid ${colors.iconBg}`,
            color: colors.iconBg,
          }}
        >
          {errorMessage || 'Operação falhou'}
        </Alert>
      </Snackbar>
    </>
  );
});

PremiumConfirmDialog.displayName = 'PremiumConfirmDialog';

export default PremiumConfirmDialog;
